/**
 * @file inward-po-list.tsx
 * @module app/modules/inward/components/inward-po-list
 * @description Inward landing screen: purchase orders a GRN can be created against
 *   (Approved / In Process), as touch-friendly cards sized for a Samsung Galaxy Tab A11.
 *   Shared by /UProcure/inward and Procurement (New) /procurement/inward.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { Badge, Button, Card, Form } from "react-bootstrap";
import { error, success } from "../../../../Global/toast";
import { useNavigate } from "react-router-dom";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import Loader from "../../../../Global/loader";
import { PermissionModule } from "../../../../utils/constants";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useAuth } from "../../auth";
import {
  canDoGrnForPo,
  canPerformInwardAction,
  InwardAction,
  isLoadingBayManagerOnly,
} from "../utils/inward-access";
import {
  canCreateGrnForPo,
  eatToday,
  formatQuantities,
  getInwardPoStatusLabel,
  getPendingQuantities,
  INWARD_ELIGIBLE_PO_STATUSES,
  InwardNextKind,
  InwardPurchaseOrder,
} from "../utils/inward-po";
import InwardTabs from "./inward-tabs";
import InwardFilterBar, { InwardFilterValue } from "./inward-filter-bar";
import InwardAssignModal from "./inward-assign-modal";
import { formatDateTime } from "../utils/inward-timeline";
import "../styles/inward.css";

const PAGE_SIZE = 12;

type StatusFilter = "all" | number;

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "all" },
  { label: "Approved", value: INWARD_ELIGIBLE_PO_STATUSES[0] },
  { label: "In Process", value: INWARD_ELIGIBLE_PO_STATUSES[1] },
];

interface InwardPoListProps {
  /** Route this list is mounted under, e.g. "/UProcure/inward" or "/procurement/inward". */
  basePath: string;
  /** Existing PO details page for this area (opened when a card is tapped). */
  poDetailsPath: string;
}

/** Colour of each "next action" chip on a PO card. */
const NEXT_TONE: Record<InwardNextKind, string> = {
  assign: "danger",
  startInward: "primary",
  inwardInProgress: "info",
  shortfallDecision: "warning",
  warehouseApproval: "warning",
  financeApproval: "warning",
  costAcknowledgement: "danger",
  complete: "success",
};

const InwardPoList = ({ basePath, poDetailsPath }: InwardPoListProps) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const canAssign = canPerformInwardAction(
    currentUser,
    InwardAction.AssignLoadingBay
  );

  const [orders, setOrders] = useState<InwardPurchaseOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<InwardFilterValue>({
    loadingBayManager: "",
    dock: "",
  });
  const [assigning, setAssigning] = useState<InwardPurchaseOrder | null>(null);
  const [reload, setReload] = useState(0);
  const [startingId, setStartingId] = useState("");
  // Loading bay managers only see their own POs for one day (default today)
  const ownDayOnly = isLoadingBayManagerOnly(currentUser);
  const [inwardDate, setInwardDate] = useState(() =>
    ownDayOnly ? eatToday() : ""
  );

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      const statuses =
        statusFilter === "all" ? INWARD_ELIGIBLE_PO_STATUSES : [statusFilter];
      const params: Record<string, string | number | boolean> = {
        sortKey: "_createdAt",
        sortOrder: -1,
        needCount: true,
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        searchTerm: search.trim(),
        ...(filters.loadingBayManager
          ? { loadingBayManager: filters.loadingBayManager }
          : {}),
        ...(filters.dock ? { dock: filters.dock } : {}),
        ...(inwardDate ? { inwardDate } : {}),
      };
      statuses.forEach((status, index) => {
        params[`status[${index}]`] = status;
      });
      const response = await new APICallService(
        INWARD.listPurchaseOrders,
        params,
        "",
        "",
        "",
        "",
        PermissionModule.Inward,
        selectedWarehouse?._id
      ).callAPI();
      setOrders(response?.records ?? []);
      setTotal(response?.total ?? 0);
      setLoading(false);
    };
    const timer = window.setTimeout(fetchOrders, search ? 400 : 0);
    return () => window.clearTimeout(timer);
  }, [
    page,
    search,
    statusFilter,
    filters,
    inwardDate,
    reload,
    selectedWarehouse?._id,
  ]);

  const openGrnForm = (poId: string) =>
    navigate(`${basePath}/create-grn`, { state: { poId } });

  /** "Start inward" on the card: records the start, then opens the form. */
  const startInward = async (po: InwardPurchaseOrder) => {
    setStartingId(po._id);
    const response = await new APICallService(
      INWARD.startInward,
      {},
      { id: po._id, action: "start" },
      "",
      "",
      "",
      PermissionModule.Inward,
      selectedWarehouse?._id
    ).callAPI();
    setStartingId("");
    if (response?.currentSession?.startedAt) {
      success(`Inward started for ${po.refKey}`);
      openGrnForm(po._id);
    } else if (response) {
      error("Inward could not be started");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="inward-page" data-testid="inward-po-list">
      <div className="inward-header">
        <div>
          <h1 className="fs-22 fw-bolder mb-1">Inward</h1>
          <p className="text-muted fs-14 mb-0" data-testid="inward-subtitle">
            {ownDayOnly
              ? `Your purchase orders to receive on ${inwardDate}`
              : "Purchase orders ready to receive goods"}
          </p>
        </div>
      </div>
      <InwardTabs basePath={basePath} active="pos" />

      <div className="inward-toolbar">
        <Form.Control
          type="search"
          className="inward-search"
          placeholder="Search PO number or supplier"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          data-testid="inward-search"
        />
        <div className="inward-filters" role="group" aria-label="PO status">
          {STATUS_FILTERS.map((filter) => (
            <Button
              key={String(filter.value)}
              variant={
                statusFilter === filter.value ? "primary" : "outline-primary"
              }
              className="inward-touch-btn"
              onClick={() => {
                setPage(1);
                setStatusFilter(filter.value);
              }}
              data-testid={`inward-filter-${filter.value}`}
            >
              {filter.label}
            </Button>
          ))}
        </div>
        <Form.Group className="inward-date-filter">
          <Form.Label className="fs-14 mb-1">Inward date</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control
              type="date"
              className="inward-input"
              value={inwardDate}
              onChange={(e) => {
                setPage(1);
                setInwardDate(e.target.value || (ownDayOnly ? eatToday() : ""));
              }}
              data-testid="inward-date"
            />
            {!ownDayOnly && inwardDate && (
              <Button
                variant="outline-secondary"
                className="inward-touch-btn"
                onClick={() => setInwardDate("")}
                data-testid="inward-date-clear"
              >
                All dates
              </Button>
            )}
          </div>
        </Form.Group>
        {!ownDayOnly && (
          <InwardFilterBar
            value={filters}
            onChange={(value) => {
              setPage(1);
              setFilters(value);
            }}
          />
        )}
      </div>

      {assigning && (
        <InwardAssignModal
          poId={assigning._id}
          poRefKey={assigning.refKey}
          current={assigning.inwardAssignment}
          onClose={() => setAssigning(null)}
          onSaved={() => {
            setAssigning(null);
            setReload((n) => n + 1);
          }}
        />
      )}

      {loading ? (
        <div className="d-flex justify-content-center p-10">
          <Loader loading={loading} />
        </div>
      ) : orders.length === 0 ? (
        <div className="inward-empty" data-testid="inward-empty">
          {ownDayOnly
            ? `No purchase orders are planned for you on ${inwardDate}.`
            : "No purchase orders are waiting for goods."}
        </div>
      ) : (
        <div className="inward-grid">
          {orders.map((po) => {
            const pending = getPendingQuantities(po);
            const openPoDetails = () =>
              navigate(poDetailsPath, {
                state: { id: po._id, status: po.status },
              });
            return (
              <Card
                key={po._id}
                className="inward-card inward-card-clickable"
                data-testid="inward-po-card"
                role="link"
                tabIndex={0}
                aria-label={`Open purchase order ${po.refKey}`}
                onClick={openPoDetails}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openPoDetails();
                  }
                }}
              >
                <Card.Body className="d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div
                        className="fs-18 fw-bold"
                        data-testid="inward-po-ref"
                      >
                        {po.refKey}
                      </div>
                      <div className="text-muted fs-14">
                        {po._createdAt ? formatDateTime(po._createdAt) : "-"}
                      </div>
                    </div>
                    <Badge
                      bg=""
                      className={`inward-badge fs-13 py-2 px-3 ${
                        po.status === INWARD_ELIGIBLE_PO_STATUSES[0]
                          ? "inward-badge--primary"
                          : "inward-badge--warning"
                      }`}
                    >
                      {getInwardPoStatusLabel(po.status)}
                    </Badge>
                  </div>

                  <dl className="inward-facts mb-0">
                    <dt>Supplier</dt>
                    <dd>
                      {po.business?.ownerName || po.business?.name || "-"}
                    </dd>
                    <dt>Pending</dt>
                    <dd data-testid="inward-po-pending">
                      {formatQuantities(pending) || "Nothing pending"}
                    </dd>
                    <dt>GRNs</dt>
                    <dd>
                      {Array.isArray(po.deliveries) ? po.deliveries.length : 0}
                    </dd>
                    <dt>Delivery</dt>
                    <dd>
                      {po.shipment?.date
                        ? formatDateTime(po.shipment.date)
                        : "-"}
                    </dd>
                    <dt>Loading bay</dt>
                    <dd data-testid="inward-po-manager">
                      {po.inwardAssignment?.loadingBayManager?.name ||
                        "Not assigned"}
                    </dd>
                    <dt>Dock</dt>
                    <dd data-testid="inward-po-dock">
                      {po.inwardAssignment?.dock?.name || "-"}
                    </dd>
                    <dt>Inward date</dt>
                    <dd data-testid="inward-po-date">
                      {po.inwardAssignment?.scheduledDate || "-"}
                    </dd>
                    <dt>Gate entry</dt>
                    <dd data-testid="inward-po-gate">
                      {po.inwardAssignment?.gateEntryNumber || "-"}
                    </dd>
                  </dl>

                  {(po.inwardNext ?? []).length > 0 && (
                    <div data-testid="inward-po-next">
                      <div className="fs-13 fw-semibold text-uppercase text-muted mb-1">
                        Pending next
                      </div>
                      <ul className="inward-next-list">
                        {(po.inwardNext ?? []).map((n, i) => (
                          <li
                            key={`${n.kind}-${n.grnRefKey ?? i}`}
                            className={`inward-next inward-next--${
                              NEXT_TONE[n.kind]
                            }`}
                            data-testid={`inward-next-${n.kind}`}
                          >
                            {n.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {po.inwardAssignment?.currentSession?.startedAt && (
                    <Badge
                      bg=""
                      className="inward-badge inward-badge--info fs-13 py-2 px-3 align-self-start"
                      data-testid="inward-po-in-progress"
                    >
                      Inward in progress since{" "}
                      {formatDateTime(
                        po.inwardAssignment.currentSession.startedAt
                      )}
                    </Badge>
                  )}

                  <div className="d-flex flex-column gap-2 mt-auto">
                    {canAssign && (
                      <Button
                        variant="outline-primary"
                        className="inward-touch-btn w-100"
                        onClick={(e) => {
                          e.stopPropagation(); // don't also open PO details
                          setAssigning(po);
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        data-testid="inward-assign"
                      >
                        {po.inwardAssignment
                          ? "Change manager, dock or date"
                          : "Plan inward (manager, dock, gate, date)"}
                      </Button>
                    )}
                    {canDoGrnForPo(currentUser, po.inwardAssignment) &&
                      canCreateGrnForPo(po) &&
                      (!po.inwardAssignment ? (
                        <div className="fs-14 text-danger fw-semibold">
                          Plan inward first (manager, dock, date)
                        </div>
                      ) : po.inwardAssignment.currentSession?.startedAt ? (
                        <Button
                          className="inward-touch-btn w-100"
                          onClick={(e) => {
                            e.stopPropagation(); // don't also open PO details
                            openGrnForm(po._id);
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                          data-testid="inward-continue"
                        >
                          Continue inward
                        </Button>
                      ) : (
                        <Button
                          variant="success"
                          className="inward-touch-btn w-100"
                          disabled={startingId === po._id}
                          onClick={(e) => {
                            e.stopPropagation(); // don't also open PO details
                            startInward(po);
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                          data-testid="inward-start-card"
                        >
                          {startingId === po._id ? "Starting…" : "Start inward"}
                        </Button>
                      ))}
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="inward-pager">
          <Button
            variant="outline-primary"
            className="inward-touch-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="fs-15 fw-semibold">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline-primary"
            className="inward-touch-btn"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default InwardPoList;
